// src/features/dashboard/pages/AtRiskAssociates.tsx

import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import {
  DashboardDateProvider,
} from '../../context/DashboardDateContext';

import AtRiskHeader from '../../components/ManagerDashboard/AtRiskAssociate/AtRiskHeader';

import AtRiskKpiCards from '../../components/ManagerDashboard/AtRiskAssociate/AtRiskKpiCards';

import AtRiskFilters from '../../components/ManagerDashboard/AtRiskAssociate/AtRiskFilters';

import RiskReasonBreakdownChart from '../../components/ManagerDashboard/AtRiskAssociate/RiskReasonBreakdownChart';

import RiskByTeamChart from '../../components/ManagerDashboard/AtRiskAssociate/RiskByTeamChart';

import FourWeekTrendChart from '../../components/ManagerDashboard/AtRiskAssociate/FourWeekTrendChart';

import AtRiskAssociateRegister from '../../components/ManagerDashboard/AtRiskAssociate/AtRiskAssociateRegister';

import useAtRiskAssociates from '../../hooks/Manager/AtRiskAssociates/useAtRiskAssociates';

import {
  getManagerEmployeeCode,
} from '../../../../features/auth/utils/Login/authStorage';

import type {
  AssociateRiskResponse,
  ReasonBreakdownResponse,
} from '../../types/Manager/atRiskAssociates.types';


// =========================================================
// NORMALIZE
// =========================================================

const normalize = (
  value?: string | null,
): string => {
  return (
    value
      ?.toString()
      .trim()
      .toLowerCase() ?? ''
  );
};


// =========================================================
// ACTION STATUS
// =========================================================
//
// IMPORTANT:
// Status filter is based on actionStatus because
// the table uses actionStatus.
// =========================================================

const getActionStatus = (
  item: AssociateRiskResponse,
): string => {
  const record =
    item as AssociateRiskResponse & {
      actionStatus?: string | null;
    };

  return (
    record.actionStatus
      ?.toString()
      .trim() ?? ''
  );
};


// =========================================================
// MAIN CONTENT
// =========================================================

const AtRiskAssociatesContent = () => {

  const managerEmployeeCode =
    getManagerEmployeeCode();

  const {
    hideLoader,
  } = useLoaderContext();


  // =======================================================
  // API
  // =======================================================

  const {
    loading,

    kpiCards,
    associateRegister,

    kpiError,
    associateError,
  } = useAtRiskAssociates({
    managerEmployeeCode,
  });


  // =======================================================
  // FILTER STATE
  // =======================================================

  const [
    selectedTeam,
    setSelectedTeam,
  ] = useState('');

  const [
    selectedRiskLevel,
    setSelectedRiskLevel,
  ] = useState('');

  const [
    selectedStatus,
    setSelectedStatus,
  ] = useState('');

  const [
    selectedShift,
    setSelectedShift,
  ] = useState('');

  const [
    selectedLocation,
    setSelectedLocation,
  ] = useState('');

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');


  // =======================================================
  // SAFE DATA
  // =======================================================

  const associates = useMemo(
    () =>
      Array.isArray(associateRegister)
        ? associateRegister
        : [],
    [associateRegister],
  );


  // =======================================================
  // TEAM OPTIONS
  // =======================================================

  const teams = useMemo(
    () => {

      return Array.from(
        new Set(
          associates
            .map(
              (item) =>
                item.teamLeadName
                  ?.toString()
                  .trim(),
            )
            .filter(Boolean),
        ),
      ).sort();

    },
    [associates],
  );


  // =======================================================
  // RISK LEVEL OPTIONS
  // =======================================================

  const riskLevels = useMemo(
    () => {

      const values =
        Array.from(
          new Set(
            associates
              .map(
                (item) =>
                  item.riskLevel
                    ?.toString()
                    .trim(),
              )
              .filter(Boolean),
          ),
        );

      const standardOrder = [
        'CRITICAL',
        'HIGH',
        'MEDIUM',
        'LOW',
      ];

      return [
        ...standardOrder.filter(
          (risk) =>
            values.some(
              (value) =>
                normalize(value) ===
                normalize(risk),
            ),
        ),

        ...values.filter(
          (value) =>
            !standardOrder.some(
              (risk) =>
                normalize(risk) ===
                normalize(value),
            ),
        ),
      ];

    },
    [associates],
  );


  // =======================================================
  // STATUS OPTIONS
  // =======================================================
  //
  // IMPORTANT:
  // Use actionStatus.
  //
  // Example:
  // COMPLETED
  // IN PROGRESS
  // PENDING
  // =======================================================

  const statuses = useMemo(
    () => {

      const values =
        Array.from(
          new Set(
            associates
              .map(
                (item) =>
                  getActionStatus(item),
              )
              .filter(Boolean),
          ),
        );

      const standardOrder = [
        'PENDING',
        'IN PROGRESS',
        'COMPLETED',
      ];

      return [
        ...standardOrder.filter(
          (status) =>
            values.some(
              (value) =>
                normalize(value) ===
                normalize(status),
            ),
        ),

        ...values.filter(
          (value) =>
            !standardOrder.some(
              (status) =>
                normalize(status) ===
                normalize(value),
            ),
        ),
      ];

    },
    [associates],
  );


  // =======================================================
  // SHIFT OPTIONS
  // =======================================================

  const shifts = useMemo(
    () => {

      return Array.from(
        new Set(
          associates
            .map(
              (item) =>
                item.shiftName
                  ?.toString()
                  .trim(),
            )
            .filter(Boolean),
        ),
      ).sort();

    },
    [associates],
  );


  // =======================================================
  // LOCATION OPTIONS
  // =======================================================

  const locations = useMemo(
    () => {

      return Array.from(
        new Set(
          associates
            .map(
              (item) =>
                item.city
                  ?.toString()
                  .trim(),
            )
            .filter(Boolean),
        ),
      ).sort();

    },
    [associates],
  );


  // =======================================================
  // FILTER ALL ASSOCIATES
  // =======================================================

  const filteredAssociates =
    useMemo(
      () => {

        const search =
          searchTerm
            .trim()
            .toLowerCase();

        return associates.filter(
          (item) => {

            // ------------------------------------------------
            // TEAM
            // ------------------------------------------------

            if (
              selectedTeam &&
              normalize(
                item.teamLeadName,
              ) !==
                normalize(
                  selectedTeam,
                )
            ) {
              return false;
            }


            // ------------------------------------------------
            // RISK LEVEL
            // ------------------------------------------------

            if (
              selectedRiskLevel &&
              normalize(
                item.riskLevel,
              ) !==
                normalize(
                  selectedRiskLevel,
                )
            ) {
              return false;
            }


            // ------------------------------------------------
            // STATUS
            // ------------------------------------------------
            //
            // Uses actionStatus
            // ------------------------------------------------

            if (selectedStatus) {

              const actionStatus =
                getActionStatus(item);

              if (
                normalize(
                  actionStatus,
                ) !==
                  normalize(
                    selectedStatus,
                  )
              ) {
                return false;
              }
            }


            // ------------------------------------------------
            // SHIFT
            // ------------------------------------------------

            if (
              selectedShift &&
              normalize(
                item.shiftName,
              ) !==
                normalize(
                  selectedShift,
                )
            ) {
              return false;
            }


            // ------------------------------------------------
            // LOCATION
            // ------------------------------------------------

            if (
              selectedLocation &&
              normalize(
                item.city,
              ) !==
                normalize(
                  selectedLocation,
                )
            ) {
              return false;
            }


            // ------------------------------------------------
            // SEARCH
            // ------------------------------------------------

            if (search) {

              const searchableText = [
                item.employeeName,
                item.employeeCode,
                item.teamLeadName,
                item.shiftName,
                item.city,
                item.riskLevel,
                item.riskFlags,
                getActionStatus(item),
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

              if (
                !searchableText.includes(
                  search,
                )
              ) {
                return false;
              }
            }


            return true;
          },
        );

      },
      [
        associates,
        selectedTeam,
        selectedRiskLevel,
        selectedStatus,
        selectedShift,
        selectedLocation,
        searchTerm,
      ],
    );


  // =======================================================
  // RISK REASON BREAKDOWN
  // =======================================================
  //
  // Recalculate from filteredAssociates.
  // Therefore Team / Risk / Status / Search all affect it.
  // =======================================================

  const filteredReasonBreakdown =
    useMemo<
      ReasonBreakdownResponse[]
    >(
      () => {

        const reasonMap =
          new Map<string, number>();


        filteredAssociates.forEach(
          (associate) => {

            const flags =
              associate.riskFlags
                ?.split(',')
                .map(
                  (flag) =>
                    flag.trim(),
                )
                .filter(Boolean) ?? [];


            flags.forEach(
              (flag) => {

                const normalized =
                  flag.toLowerCase();

                let reason =
                  flag;


                if (
                  normalized.includes(
                    'low productivity',
                  )
                ) {

                  reason =
                    'Low Productivity';

                } else if (
                  normalized.includes(
                    'high idle',
                  )
                ) {

                  reason =
                    'High Idle';

                } else if (
                  normalized.includes(
                    'low active',
                  )
                ) {

                  reason =
                    'Low Active Hrs';

                } else if (
                  normalized.includes(
                    'non prod',
                  ) ||
                  normalized.includes(
                    'non-product',
                  )
                ) {

                  reason =
                    'Non Prod Apps';

                } else if (
                  normalized.includes(
                    'attendance',
                  )
                ) {

                  reason =
                    'Attendance';
                }


                reasonMap.set(
                  reason,
                  (
                    reasonMap.get(
                      reason,
                    ) ?? 0
                  ) + 1,
                );

              },
            );

          },
        );


        const order = [
          'Low Productivity',
          'High Idle',
          'Low Active Hrs',
          'Non Prod Apps',
          'Attendance',
        ];


        return order
          .filter(
            (reason) =>
              reasonMap.has(
                reason,
              ),
          )
          .map(
            (reason) => ({
              reason,
              count:
                reasonMap.get(
                  reason,
                ) ?? 0,
            }),
          );

      },
      [filteredAssociates],
    );


  // =======================================================
  // RISK BY TEAM
  // =======================================================
  //
  // IMPORTANT:
  // Do NOT return TeamRiskResponse here.
  //
  // RiskByTeamChart expects:
  // {
  //   team: string;
  //   count: number;
  // }
  // =======================================================

  const filteredTeamData =
    useMemo(
      () => {

        const teamMap =
          new Map<string, number>();


        filteredAssociates.forEach(
          (associate) => {

            const team =
              associate.teamLeadName
                ?.toString()
                .trim();

            if (!team) {
              return;
            }

            teamMap.set(
              team,
              (
                teamMap.get(
                  team,
                ) ?? 0
              ) + 1,
            );

          },
        );


        return Array.from(
          teamMap.entries(),
        ).map(
          ([
            team,
            count,
          ]) => ({
            team,
            count,
          }),
        );

      },
      [filteredAssociates],
    );


  // =======================================================
  // RESET
  // =======================================================

  const handleReset = () => {

    setSelectedTeam('');
    setSelectedRiskLevel('');
    setSelectedStatus('');
    setSelectedShift('');
    setSelectedLocation('');
    setSearchTerm('');

  };


  // =======================================================
  // LOADER
  // =======================================================

  useEffect(
    () => {

      if (!loading) {

        const timer =
          setTimeout(
            () => {
              hideLoader();
            },
            500,
          );

        return () =>
          clearTimeout(timer);
      }

    },
    [
      loading,
      hideLoader,
    ],
  );


  // =======================================================
  // LOADING
  // =======================================================

  if (loading) {
    return null;
  }


  // =======================================================
  // MANAGER CODE
  // =======================================================

  if (!managerEmployeeCode) {

    return (
      <div
        className="
          flex
          h-[70vh]
          items-center
          justify-center
          text-red-600
        "
      >
        Manager Employee Code not found
        in Local Storage.
      </div>
    );
  }


  // =======================================================
  // UI
  // =======================================================

  return (
    <div className="space-y-5">

      <DashboardHeader
        showBackButton
        breadcrumbs={[
          {
            label: 'Work Vista',
            path: '/',
          },
          {
            label:
              'Reporting Manager Dashboard',
           path: '/rm-dashboard',
          },
          {
            label:
              'At-Risk Associates',
          },
        ]}
      />


      <div
        className="
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-5
        "
      >

        <AtRiskHeader />


        {/* KPI */}

        <AtRiskKpiCards
          data={kpiCards}
          error={kpiError}
        />


        {/* FILTERS */}

        <AtRiskFilters
          teams={teams}
          riskLevels={riskLevels}
          statuses={statuses}
          shifts={shifts}
          locations={locations}

          selectedTeam={
            selectedTeam
          }

          selectedRiskLevel={
            selectedRiskLevel
          }

          selectedStatus={
            selectedStatus
          }

          selectedShift={
            selectedShift
          }

          selectedLocation={
            selectedLocation
          }

          searchTerm={
            searchTerm
          }

          onTeamChange={
            setSelectedTeam
          }

          onRiskLevelChange={
            setSelectedRiskLevel
          }

          onStatusChange={
            setSelectedStatus
          }

          onShiftChange={
            setSelectedShift
          }

          onLocationChange={
            setSelectedLocation
          }

          onSearchChange={
            setSearchTerm
          }

          onReset={
            handleReset
          }
        />


        {/* =================================================
            CHARTS
        ================================================= */}

        <div
          className="
            mt-5
            grid
            grid-cols-1
            gap-4
            xl:grid-cols-12
          "
        >

          {/* Risk Reason */}

          <div className="xl:col-span-5">

            <RiskReasonBreakdownChart
              data={
                filteredReasonBreakdown
              }
              error={null}
            />

          </div>


          {/* Risk By Team */}

          <div className="xl:col-span-4">

            <RiskByTeamChart
              data={
                filteredTeamData
              }
              error={null}
            />

          </div>


          {/* Four Week Trend */}

          <div className="xl:col-span-3">

            <FourWeekTrendChart
              managerEmployeeCode={
                managerEmployeeCode
              }
            />

          </div>

        </div>


        {/* =================================================
            TABLE
        ================================================= */}

        <div className="mt-5">

          <AtRiskAssociateRegister
            data={
              filteredAssociates
            }
            error={
              associateError
            }
          />

        </div>

      </div>

    </div>
  );
};


// =========================================================
// PROVIDER
// =========================================================

const AtRiskAssociates = () => {

  return (
    <DashboardDateProvider>
      <AtRiskAssociatesContent />
    </DashboardDateProvider>
  );
};


export default AtRiskAssociates;